import {use, useEffect, useState} from 'react'
import {Checkbox} from '@primer/react-brand/esm'

import ConsentLanguage from './ConsentLanguage/ConsentLanguage'
import {ConsentValue} from './types'
import {FormContext} from '../../FormContext'
import {getAnalyticsEvent} from '../../../../../lib/utils/analytics'
import type {Country} from './types'

const IMPLICIT_CONSENT_COUNTRIES = ['US']

export interface ConsentExperienceProps {
  fieldName: string
  selectedCountry: Country
  privacyStatementHref: string
  emailSubscriptionSettingsLinkHref: string
  hasPhone?: boolean
  /**
   * List of fields that will be used as examples in the consent language for South Korea. For example, if the form
   * contains a field for the user's first name, last name, and email address, you may pass
   * in `['first name', 'last name', 'email']`.
   */
  exampleFields: string[]
  /**
   * Optional callback that will be called if the consent experience changes their validation state.
   * Some countries require additional explicit consent for the privacy statement (e.g. South Korea).
   *
   * This is helpful to validate the form programmatically before submission from the parent component.
   */
  onValidationChange?: (isValid: boolean) => void
  /**
   * If true, the field will only be sent if the checkbox is checked. Defaults to `false`.
   */
  onlySendIfChecked?: boolean
  /**
   * @see ConsentExperienceProps.implicitOptIn
   */
  implicitOptIn: boolean
}

export function ConsentExperienceInput({
  selectedCountry,
  fieldName,
  onValidationChange,
  implicitOptIn,
  ...passThruProps
}: ConsentExperienceProps) {
  const [consentChecked, setConsentChecked] = useState(false)
  const [showCheckbox, setShowCheckbox] = useState(true)
  const formContext = use(FormContext)

  const {
    onChange: marketingOptInOnChange,
    ref,
    ...registerMarketingEmailOptInProps
  } = formContext?.register(fieldName, {
    label: 'Marketing Email Opt In',
    required: false,
  }) ?? {}

  /**
   * Unless the underlying ConsentLanguage component says otherwise, we assume
   * the consent experience is valid by default.
   */
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(true)
    }
  }, [onValidationChange])

  /**
   * When `selectedCountry` or `implicitOptIn` change, decide whether to display the explicit
   * consent checkbox or rely on implicit consent. This effect only runs when the `implicitOptIn` prop
   * is true. For countries listed in `IMPLICIT_CONSENT_COUNTRIES` we hide the checkbox and
   * submit a hidden input representing implicit consent (`ConsentValue.IMPLICIT`); for all
   * other countries we show the explicit checkbox so users can opt in.
   */
  useEffect(() => {
    if (!implicitOptIn) return

    if (IMPLICIT_CONSENT_COUNTRIES.includes(selectedCountry.alpha)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowCheckbox(false)
    } else {
      setShowCheckbox(true)
    }
  }, [selectedCountry, implicitOptIn])

  return (
    <div data-testid="consent-experience">
      <ConsentLanguage
        fieldName={fieldName}
        onValidationChange={onValidationChange}
        implicitOptIn={implicitOptIn}
        {...passThruProps}
      >
        {showCheckbox ? (
          <Checkbox
            value={ConsentValue.EXPLICIT}
            checked={consentChecked}
            hidden={!showCheckbox}
            onChange={event => {
              marketingOptInOnChange?.(event)
              setConsentChecked(!consentChecked)
            }}
            ref={ref}
            {...getAnalyticsEvent({
              action: 'consent_language',
              tag: 'checkbox',
              context: 'form_control',
              location: 'form',
            })}
            {...registerMarketingEmailOptInProps}
          />
        ) : (
          <input type="hidden" name={fieldName} value={ConsentValue.IMPLICIT} ref={ref} />
        )}
      </ConsentLanguage>
    </div>
  )
}
